import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import AccessoryLanding from './AccessoryLanding';
import AccessoryActivationFlow from './AccessoryActivationFlow';
import { handleAccessoryScan } from 'apis/accessoryApi';
import MobileNotification from 'components/common/notification/MobileNotification';
import { isMobileDevice } from 'utils/deviceDetector';

const AccessoryWorkFlow = () => {
  const { serialNumber } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessoryData, setAccessoryData] = useState(null);
  const [notFoundError, setNotFoundError] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const isMobile = isMobileDevice();

  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const checkAccessoryStatus = async () => {
      setLoading(true);
      setAccessoryData(null);
      setNotFoundError(false);

      if (location.pathname === '/accessory') {
        setLoading(false);
        return;
      }

      if (!serialNumber) {
        navigate('/accessory');
        return;
      }

      try {
        const scanResponse = await handleAccessoryScan(serialNumber, 'accessory');

        if (!scanResponse || !scanResponse.success || !scanResponse.accessory) {
          setNotFoundError(true);
          setNotification({
            open: true,
            message: 'Invalid serial number. This accessory does not exist in our system.',
            severity: 'error'
          });
          setLoading(false);
          return;
        }

        const accessory = scanResponse.accessory;

        if (
          accessory.status === 'activated' &&
          accessory.content?.data?.cardId
        ) {
          const cardUrl = `/card/${accessory.content.data.cardId}?source=accessory`;
          navigate(cardUrl);
          return;
        }

        setAccessoryData(accessory);
      } catch (error) {
        setNotification({
          open: true,
          message: 'Failed to load accessory data. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    checkAccessoryStatus();
  }, [serialNumber, navigate, location.pathname]);






  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking accessory...
        </Typography>
      </Box>
    );
  }

  if (location.pathname === '/accessory') {
    return <AccessoryLanding />;
  }

  if (!accessoryData || notFoundError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: 3,
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" gutterBottom color="error">
          Invalid Serial Number
        </Typography>
        <Typography variant="body1" gutterBottom>
          This accessory does not exist in our system.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please check the serial number and try again
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/accessory')}
          sx={{ mb: 3 }}
        >
          Return to Home
        </Button>
      </Box>
    );
  }

  return (
    <>
      <AccessoryActivationFlow
        initialSerialNumber={serialNumber}
        accessoryData={accessoryData}
        isMobile={isMobile}
      />
      <MobileNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleNotificationClose}
      />
    </>
  );
};

export default AccessoryWorkFlow;